import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  DataGrid,
  Column,
  Scrolling,
  Paging,
} from "devextreme-react/data-grid";
import { useDataGridColumnWidths } from "../../../hooks/useDataGridColumnWidths";
import { MasterDetailTemplateData } from "devextreme/ui/data_grid";
import {
  createSitesClientApi,
  taxPortalClientApi,
  userAssignmentClientApi,
} from "../../../api-client-nswag/taxportal-client-runtime";
import {
  GetAllRolesDTO,
  GetAllSitesInfoDTO,
  GetSiteInfoDTO,
  ServiceBusEnum,
  ServiceMessage,
  SiteTrackRecordDTO,
  SyncTypeEnum,
} from "../../../api-client-nswag/taxportal-client";
import { UserAssignTpac, TpacRequestBasicMessage } from "../../../api-client-nswag/userassignment-client";
import {
  atom,
  selector,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { Button, Link } from "@mui/material";
import { dataGridConfig } from "../../../constants";
import { SnackbarMessage, useSnackbar } from "../../../hooks";
import { OidcIdentityContext } from "@rsmus/react-auth";
import { getExpandedRowData, getRoleData } from "../CreateSite&SetUp";
import { isEmpty, isNull } from "lodash";
import { convertUTCTimeToLocal } from "../../../util/Utils";
import { getAppName } from "../../../cui/commonComponents/AppNameToggle";
import Roles from "../../Roles/Roles";
import { ProcessListTab } from "./ProcessListTab";

interface dataTypes {
  pending: boolean;
  data: SiteTrackRecordDTO[] | undefined;
  fullfilled: boolean;
}

export const statusStateForCS = atom({
  key: "statusState1",
  default: { siteId: null, status: "Active" },
});
interface statusType {
  siteId: number | null | any;
  status: string;
}
interface roleType {
  name?: string | undefined;
  RoleId?: string | undefined;
}

export const SiteProcessDataForCS = (props: MasterDetailTemplateData) => {
  const [data, setData] = useState<dataTypes>({
    pending: false,
    data: [],
    fullfilled: false,
  });
  const { user, logout } = useContext(OidcIdentityContext);
  const siteId = useRecoilValue(getExpandedRowData);
  const { showSnackbar } = useSnackbar();
  const [status, setStatus] = useRecoilState<statusType>(statusStateForCS);
  const appName = useRecoilValue(getAppName);
  const roles = useRecoilValue(getRoleData);
  const [selectedTab, setSelectedTab] = useState<string>('tab1');

    // Global column width management
  const { gridRef, onColumnResized, onInitialized } = useDataGridColumnWidths({
    gridId: `siteProcessDataForCS-${siteId}`,
    defaultColumnWidths: {
      0: 400, // process column
      1: 100, // Run Process column  
      2: 80,  // Status column
      3: 400, // Message column
      4: 200, // Initiated date and time column
      5: 200, // Completed date and time column
      6: 150  // Initiated by column (default width)
    }
  });
  const processToInclude = [
    "CLIENT_LEVEL_ROLE_ASSIGN",
    "INTERNAL_USER_ASSIGNMENT_CS",
    "CREATE_EXTERNAL_USER_CS",
    "CHECK_EXTERNAL_USER_STATUS",
    "MEMBER_FIRM",
    "CREATE_EXTERNAL_USER_THIRD_PARTY",
    "CHECK_THIRD_PARTY_USER_STATUS",
  ];

  const subProcessToInclude = [
    "L2Support",
    "CLIENT_USER",
    "RSM_ENGAGEMENT_TEAM",
    "FUNCTIONAL_LEAD",
    "PROJECT_LEAD",
    "MEMBER_FIRM",
    "MEMBER_FIRM_STATUS",
    "CS_ENGAGEMENT_TEAM",
    "CS_FUNCTIONAL_LEAD",
    "CS_PROJECT_LEAD",
    "CS_POWER_USER_DELEGATED",
    "ASSIGN_USER_TO_SITE",
  ];
  const SYNC_ISHEET = "Sync ISheet";
  const SYNC_FOLDER_METADATA_ISHEET = "Folder metadata";
  const CREATE_EXTERNAL_USER_CS = "CREATE_EXTERNAL_USER_CS";
  const CLIENT_LEVEL_ROLE_ASSIGN = "CLIENT_LEVEL_ROLE_ASSIGN";
  const INTERNAL_USER_ASSIGNMENT_CS = "INTERNAL_USER_ASSIGNMENT_CS";
  const CS_L2Support = "CS_L2Support";
  const CS_ISHEET_DEFAULT_DATA = "CS_ISHEET_DEFAULT_DATA";
  const MEMBER_FIRM = "MEMBER_FIRM";
  const PROCESS_TYPES = {
    CS_ISHEET_DEFAULT_DATA,
    CREATE_EXTERNAL_USER_CS,
    CLIENT_LEVEL_ROLE_ASSIGN,
    INTERNAL_USER_ASSIGNMENT_CS,
    CHECK_EXTERNAL_USER_STATUS: "CHECK_EXTERNAL_USER_STATUS",
    MEMBER_FIRM,
    CREATE_EXTERNAL_USER_THIRD_PARTY: "CREATE_EXTERNAL_USER_THIRD_PARTY",
  };
  const SUBTYPES = {
    CS_L2Support,
    TRANSITION_PLAN: "TRANSITION_PLAN",
    DELIVERY_PLAN: "DELIVERY_PLAN",
    PROJECT_SETUP_TASK_TEMPLATES: "PROJECT_SETUP_TASK_TEMPLATES",
    ANALYTIC_INSIGHT_SETUP: "ANALYTIC_INSIGHT_SETUP",
    IDM_AND_CEM: "IDM_AND_CEM",
    MEMBER_FIRM_STATUS: "MEMBER_FIRM_STATUS",
  };

  const createProcess = (
    type: string,
    subType?: string
  ): SiteTrackRecordDTO => ({
    type,
    status: "",
    message: "",
    createdBy: "",
    created: undefined,
    lastModified: undefined,
    subType,
  });

  const ensureProcessExists = (
    processes: SiteTrackRecordDTO[],
    type: string,
    subTypes: string[] = []
  ) => {
    if (!processes.some((e) => e.type === type)) {
      if (subTypes.length > 0) {
        subTypes.forEach((subType) =>
          processes.push(createProcess(type, subType))
        );
      } else {
        processes.push(createProcess(type));
      }
    }
  };

  const ensureRequiredProcesses = (processes: SiteTrackRecordDTO[]) => {
    ensureProcessExists(processes, PROCESS_TYPES.CS_ISHEET_DEFAULT_DATA, [
      SUBTYPES.TRANSITION_PLAN,
      SUBTYPES.DELIVERY_PLAN,
      SUBTYPES.PROJECT_SETUP_TASK_TEMPLATES,
      SUBTYPES.ANALYTIC_INSIGHT_SETUP,
    ]);
    ensureProcessExists(
      processes,
      PROCESS_TYPES.CREATE_EXTERNAL_USER_THIRD_PARTY,
      [SUBTYPES.IDM_AND_CEM]
    );
    ensureProcessExists(processes, PROCESS_TYPES.CREATE_EXTERNAL_USER_CS, [
      SUBTYPES.IDM_AND_CEM,
    ]);
    ensureProcessExists(processes, PROCESS_TYPES.MEMBER_FIRM, [
      SUBTYPES.IDM_AND_CEM,
      SUBTYPES.MEMBER_FIRM_STATUS,
    ]);

    ensureProcessExists(processes, PROCESS_TYPES.CLIENT_LEVEL_ROLE_ASSIGN, [
      SUBTYPES.CS_L2Support,
    ]);

    ensureProcessExists(processes, PROCESS_TYPES.CHECK_EXTERNAL_USER_STATUS);
  };

  const filterProcesses = (processes: SiteTrackRecordDTO[]) => {
    return processes.filter((item: any) => {
      const includeProcess = processToInclude.includes(item.type);
      const clientLevelRoleCondition =
        item.type === CLIENT_LEVEL_ROLE_ASSIGN &&
        (item.subType === null || subProcessToInclude.includes(item.subType));
      const createExternalUserCondition =
        (item.type === "CREATE_EXTERNAL_USER_CS" ||
          item.type === "MEMBER_FIRM" ||
          item.type === "CREATE_EXTERNAL_USER_THIRD_PARTY") &&
        (item.subType === null || item.subType === "ASSIGN_USER_TO_SITE");
      const excludeTessCondition =
        item.type === CLIENT_LEVEL_ROLE_ASSIGN &&
        (item.subType === "TESS" || item.subType === "POWER_USER_DELEGATED");

      return (
        includeProcess &&
        !clientLevelRoleCondition &&
        !createExternalUserCondition &&
        !excludeTessCondition
      );
    });
  };

  const processData = async () => {
    try {
      if (siteId !== null) {
        setData({ pending: true, data: [], fullfilled: false });
        const processDataStatus =
          await taxPortalClientApi.siteSetUp_SiteSetUpStatus(siteId);
        const processes = processDataStatus?.data || [];
        ensureRequiredProcesses(processes);
        const filteredProcesses = filterProcesses(processes);
        [SUBTYPES.CS_L2Support].forEach((subType) => {
          if (
            !filteredProcesses.some(
              (e) =>
                e.type === CLIENT_LEVEL_ROLE_ASSIGN && e.subType === subType
            )
          ) {
            filteredProcesses.push(
              createProcess(CLIENT_LEVEL_ROLE_ASSIGN, subType)
            );
          }
        });

        [SUBTYPES.IDM_AND_CEM, SUBTYPES.MEMBER_FIRM_STATUS].forEach(
          (subType) => {
            if (
              !filteredProcesses.some(
                (e) => e.type === MEMBER_FIRM && e.subType === subType
              )
            ) {
              filteredProcesses.push(createProcess(MEMBER_FIRM, subType));
            }
          }
        );

        setData({ pending: false, data: filteredProcesses, fullfilled: true });
        const response =
          await createSitesClientApi.taxportal_GetSiteProcessExecutionDetails(
            String(siteId),
            appName
          );
        const parentStatus = response?.data?.status;
        if (typeof parentStatus !== "string" || !parentStatus.trim()) {
          throw new Error("Process Execution API returned no parent status.");
        }
        setStatus({ siteId, status: parentStatus });
      }
    } catch (error) {
      console.error("Error processing data:", error);
      setData({ pending: false, data: [], fullfilled: false });
    }
  };

  useEffect(() => {
    if (siteId !== null) {
      processData();
    }
  }, []);

  const cellFunction1 = (celldata: any) => {
    const type = celldata.data.type;
    const subType = celldata.data.subType;

    const specialTypeMapping: { [key: string]: string } = {
      "MEMBER_FIRM-IDM_AND_CEM":
        "Assign Member Firm User - Member Firm User Request Form",
      "MEMBER_FIRM-MEMBER_FIRM_STATUS":
        "Member Firm User - Update Status",
      "CREATE_EXTERNAL_USER_THIRD_PARTY-IDM_AND_CEM":
        "Assign External User - Third Party User Request Form",
      "CHECK_THIRD_PARTY_USER_STATUS-CHECK_THIRD_PARTY_USER_STATUS":
        "Third Party User - Update Status",
    };

    const key = type + (subType ? `-${subType}` : "");
    if (specialTypeMapping[key]) {
      return specialTypeMapping[key];
    }
    const typeMapping: { [key: string]: string } = {
      SYNC_ISHEET: "Sync ISheet",
      SYNC_MDM: "Sync MDM Entity & Consent",
      SYNC_MDM_PROJECT: "Sync MDM Projects",
      SYNC_MDM_PHASE: "Sync MDM Phases",
      SYNC_IMANAGE_WORKSPACE_PER_SITE:
        "Sync IManage Workspaces & Update Folder Metadata",
      SYNC_FOLDER_METADATA_ISHEET: "Sync Folder Metadata ISheet",
      RUN_SITES_API: "Run Sites API",
      RUN_ISHEET_TRIGGER_API: "Run Isheet Trigger API",
      POPULATE_MEMBERFIRM_DROPDOWN: "Populate Member Firm Organization",
      PUSH_ORG_TO_ISHEET: "Populate Third-party Organization",
      SITE_CREATION: "Create Site from template",
      CS_ISHEET_DEFAULT_DATA: `Populate Isheet - ${subType}`,
      CREATE_EXTERNAL_USER_CS: `Assign External User - Client User Request Form`,
      CLIENT_LEVEL_ROLE_ASSIGN: `Assign Internal User - ${subType}`,
      INTERNAL_USER_ASSIGNMENT_CS:
        "Assign Internal- Internal User Request Form",
      CHECK_EXTERNAL_USER_STATUS: "External User - Update Status",
    };
    return typeMapping[type] || "Unknown Type";
  };

  const customOrderCS = [
    "SITE_CREATION",
    "SYNC_ISHEET",
    "SYNC_MDM",
    "SYNC_MDM_PROJECT",
    "SYNC_MDM_PHASE",
    "SYNC_IMANAGE_WORKSPACE_PER_SITE",
    "SYNC_FOLDER_METADATA_ISHEET",
    "RUN_SITES_API",
    "RUN_ISHEET_TRIGGER_API",
    "POPULATE_MEMBERFIRM_DROPDOWN",
    "PUSH_ORG_TO_ISHEET",
    "CS_ISHEET_DEFAULT_DATA",
    "CLIENT_LEVEL_ROLE_ASSIGN",
    "INTERNAL_USER_ASSIGNMENT_CS",
    "CREATE_EXTERNAL_USER_CS",
    "CHECK_EXTERNAL_USER_STATUS",
    "MEMBER_FIRM",
    "CREATE_EXTERNAL_USER_THIRD_PARTY",
    "CHECK_THIRD_PARTY_USER_STATUS",
  ];
  const sortedDataSourceforCS = data?.data?.sort((a, b) => {
    return customOrderCS.indexOf(a.type!) - customOrderCS.indexOf(b.type!);
  });

  const isSyncMdmInProgressOrError = () => {
    return sortedDataSourceforCS?.some((e) => {
      return (
        ["SYNC_MDM", "SYNC_MDM_PHASE", "SYNC_MDM_PROJECT"].includes(e?.type!) &&
        (e.status === "ERROR" || e.status === "INPROGRESS" || e.status === null)
      );
    });
  };

  const retryButton = (celldata: any) => {
    const type = celldata.data.type;
    const subType = celldata.data.subType;
    const status = celldata.data.status;
    const roleNames = celldata.data.subType;
    const roleId = roles?.find((role: GetAllRolesDTO) => {
      return (
        role?.name?.toLowerCase().replace(/[^a-z0-9]/g, "") ===
        roleNames?.toLowerCase().replace(/[^a-z0-9]/g, "")
      );
    })?.roleId;
    const showRetry = () => {
      return (
        type === "SITE_CREATION" &&
        (status === null || status === "INPROGRESS" || status === "ERROR")
      );
    };
    const showRun = () =>
      type === "SYNC_MDM" ||
      type === "SYNC_MDM_PROJECT" ||
      type === "SYNC_MDM_PHASE" ||
      type === "SYNC_IMANAGE_WORKSPACE_PER_SITE" ||
      type === "SYNC_ISHEET" ||
      type === "SYNC_FOLDER_METADATA_ISHEET" ||
      type === "RUN_ISHEET_TRIGGER_API" ||
      type === "RUN_SITES_API" ||
      type === "POPULATE_MEMBERFIRM_DROPDOWN" ||
      type === "PUSH_ORG_TO_ISHEET" ||
      type === "CS_ISHEET_DEFAULT_DATA" ||
      type === "CREATE_EXTERNAL_USER_CS" ||
      type === "CHECK_EXTERNAL_USER_STATUS" ||
      type === "INTERNAL_USER_ASSIGNMENT_CS" ||
      type === "CLIENT_LEVEL_ROLE_ASSIGN" ||
      type === "MEMBER_FIRM" ||
      type === "CREATE_EXTERNAL_USER_THIRD_PARTY" ||
      type === "CHECK_THIRD_PARTY_USER_STATUS";

    const retryProcess = async (celldata: any) => {
      const syncType = (() => {
        if (type === "SYNC_MDM") return ServiceBusEnum.SYNC_MDM;
        if (type === "CS_ISHEET_DEFAULT_DATA")
          return ServiceBusEnum.CS_ISHEET_DEFAULT_DATA;
        if (type === "CLIENT_LEVEL_ROLE_ASSIGN" && subType === "CS_L2Support") {
          return null; // Only call userAssignment_DirectRoleAssignInitiate, no other API calls
        }
        if (type === "CREATE_EXTERNAL_USER_CS" && subType === "IDM_AND_CEM") {
          return ServiceBusEnum.CREATE_EXTERNAL_USER_CS;
        }
        if (
          type === "CREATE_EXTERNAL_USER_CS" &&
          subType === "ASSIGN_USER_TO_SITE"
        ) {
          return ServiceBusEnum.CREATE_EXTERNAL_USER_CS;
        }
        if (type === "MEMBER_FIRM" && subType === "IDM_AND_CEM") {
          return ServiceBusEnum.MEMBER_FIRM;
        }
        if (type === "MEMBER_FIRM" && subType === "ASSIGN_USER_TO_SITE") {
          return ServiceBusEnum.MEMBER_FIRM;
        }
        if (type === "MEMBER_FIRM" && subType === "MEMBER_FIRM_STATUS") {
          return ServiceBusEnum.CHECK_EXTERNAL_USER_STATUS;
        }
        if (type === "CHECK_EXTERNAL_USER_STATUS")
          return ServiceBusEnum.CHECK_EXTERNAL_USER_STATUS;
        if (type === "CS_ISHEET_DEFAULT_DATA")
          return ServiceBusEnum.CS_ISHEET_DEFAULT_DATA;
        if (type === INTERNAL_USER_ASSIGNMENT_CS)
          return ServiceBusEnum.INTERNAL_USER_ASSIGNMENT_CS;
        if (type === "RUN_SITES_API") return ServiceBusEnum.RUN_SITES_API;
        if (type === "RUN_ISHEET_TRIGGER_API")
          return ServiceBusEnum.RUN_ISHEET_TRIGGER_API;
        if (type === "POPULATE_MEMBERFIRM_DROPDOWN")
          return ServiceBusEnum.POPULATE_MEMBERFIRM_DROPDOWN;
        if (type === "PUSH_ORG_TO_ISHEET")
          return ServiceBusEnum.PUSH_ORG_TO_ISHEET;
        if (type === "SYNC_MDM_PROJECT") return ServiceBusEnum.SYNC_MDM_PROJECT;
        if (type === "SYNC_MDM_PHASE") return ServiceBusEnum.SYNC_MDM_PHASE;
        if (type === "CREATE_EXTERNAL_USER_THIRD_PARTY")
          return ServiceBusEnum.CREATE_EXTERNAL_USER_THIRD_PARTY;
        if (type === "CHECK_THIRD_PARTY_USER_STATUS")
          return ServiceBusEnum.CHECK_EXTERNAL_USER_STATUS;
        if (type === "SYNC_IMANAGE_WORKSPACE_PER_SITE")
          return ServiceBusEnum.SYNC_IMANAGE_WORKSPACE_PER_SITE;
        return null;
      })();

      const assignmentType = (() => {
        if (subType === "ASSIGN_USER_TO_SITE") {
          return "Assign User to site";
        }
        if (subType === "IDM_AND_CEM") return "IDM and CEM";
        if (type === "CHECK_EXTERNAL_USER_STATUS") return "Check Status";
        if (subType === "MEMBER_FIRM_STATUS") return "Check Status";
      })();

      const isheetType = (() => {
        if (type === "MEMBER_FIRM")
          return "Member Firm User Request Form";
        if (type === "CREATE_EXTERNAL_USER_CS")
          return "Client User Requests";
        if (type === "INTERNAL_USER_ASSIGNMENT_CS")
          return "Internal User Request Form";
        if (type === "CHECK_EXTERNAL_USER_STATUS") return "Client User Requests";
        if(type ==="CHECK_THIRD_PARTY_USER_STATUS") return "Third-party User Request Form";
        if (
          type === "CS_ISHEET_DEFAULT_DATA" &&
          subType === "ANALYTIC_INSIGHT_SETUP"
        )
          return "Analytic Insight Setup";
        if (
          type === "CS_ISHEET_DEFAULT_DATA" &&
          subType === "PROJECT_SETUP_TASK_TEMPLATES"
        )
          return "Project Setup – Task Templates";
        if (type === "CS_ISHEET_DEFAULT_DATA" && subType === "DELIVERY_PLAN")
          return "Delivery Plan";
        if (type === "CS_ISHEET_DEFAULT_DATA" && subType === "TRANSITION_PLAN")
          return "Transition Plan";

        if (
          type === "CREATE_EXTERNAL_USER_THIRD_PARTY" &&
          subType === "IDM_AND_CEM"
        )
          return "Third-party User Request Form";
        if (
          type === "CREATE_EXTERNAL_USER_THIRD_PARTY" &&
          subType === "ASSIGN_USER_TO_SITE"
        )
          return "Third-party User Request Form";
        if (
          type === "CHECK_THIRD_PARTY_USER_STATUS" &&
          subType === "CHECK_THIRD_PARTY_USER_STATUS"
        )
          return "Third-party User Request Form";
        if (type === "SYNC_FOLDER_METADATA_ISHEET") return "Folder metadata";
      })();

      const syncIsheets = (() => {
        if (type === "SYNC_ISHEET") return "Sync Isheet";
        if (type === "SYNC_FOLDER_METADATA_ISHEET") return "Folder metadata";
      })();

      const roleType = (() => {
        if (subType === "CS_L2Support") {
          return "CS - L2 Support";
        }
      })();
      // Handle CS_L2Support separately (only calls DirectRoleAssignInitiate)
      if (type === "CLIENT_LEVEL_ROLE_ASSIGN" && subType === "CS_L2Support") {
        try {
          const roleName = "CS - L2 Support";
          const payloadForL2Support = {
            hqSiteId: props?.data?.data?.hqSiteId,
            userEmail: user?.profile.preferred_username,
            roleName: roleName,
            action: 0, // UserAssignmentCEMAction.Add
            appName: appName,
            initiatedBy: user?.profile.preferred_username,
            userAssignmentStepName: "Init_DirectRoleAssignUnAssign",
            Source: "TPAC",
          };
          await userAssignmentClientApi.userAssignment_DirectRoleAssignInitiate(payloadForL2Support);
        } catch (error) {
          showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        }
        return; // Exit early, don't process other logic
      }

      if (syncType) {
        const data: ServiceMessage = {
          siteId: props?.data?.data?.hqSiteId,
          userName: user?.profile.preferred_username,
          eventType: syncType,
          externalUserType: assignmentType,
          roleName: roleType,
          roleId: roleId,
          isheetType: isheetType,
          mdmClientId: props?.data?.data?.mdmClientId,
          clientName: props?.data?.data?.name,
          appName: appName,
        };
        const payload: TpacRequestBasicMessage[] = [
          {
            hqSiteId: props?.data?.data?.hqSiteId,
            appName: appName,
            iSheetType: isheetType!,
          },
        ];
        try {
          // Handle the three main user assignment processes with UserAssignTpacTrigger
          if (syncType === ServiceBusEnum.INTERNAL_USER_ASSIGNMENT_CS && !subType) {
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerUserAssign,
              payload
            );
          } else if (syncType === ServiceBusEnum.CREATE_EXTERNAL_USER_CS && !subType) {
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerUserAssign,
              payload
            );
          } else if (syncType === ServiceBusEnum.CREATE_EXTERNAL_USER_THIRD_PARTY && !subType) {
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerUserAssign,
              payload
            );
          } else if (
            syncType === ServiceBusEnum.RUN_SITES_API ||
            syncType === ServiceBusEnum.RUN_ISHEET_TRIGGER_API
          ) {
            await taxPortalClientApi.siteSetUp_SitesAPI(data);
          } else if (syncType === ServiceBusEnum.CREATE_EXTERNAL_USER_CS) {
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerUserAssign,
              payload
            );
          } else if (syncType === ServiceBusEnum.MEMBER_FIRM) {
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerUserAssign,
              payload
            );
          } else if (syncType === ServiceBusEnum.CREATE_EXTERNAL_USER_THIRD_PARTY) {
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerUserAssign,
              payload
            );
          } else if(syncType === ServiceBusEnum.CHECK_EXTERNAL_USER_STATUS){
            await userAssignmentClientApi.userAssignment_UserAssignTpacTrigger(
              UserAssignTpac.TriggerCheckStatus,
              payload
            );
          } else {
            await taxPortalClientApi.taxPortal_InsertTopic([data]);
          }
          if (syncType === ServiceBusEnum.SYNC_MDM) {
            showSnackbar({ message: SnackbarMessage.SYNC_MDM });
          }
          if (syncType === ServiceBusEnum.POPULATE_MEMBERFIRM_DROPDOWN) {
            showSnackbar({ message: SnackbarMessage.POPULATE_MEMBER_FIRM_ORG });
          }
          if (syncType === ServiceBusEnum.PUSH_ORG_TO_ISHEET) {
            showSnackbar({ message: SnackbarMessage.POPULATE_THIRD_PARTY_ORG });
          }
          if (
            syncType === ServiceBusEnum.CS_ISHEET_DEFAULT_DATA &&
            subType === "ANALYTIC_INSIGHT_SETUP"
          ) {
            showSnackbar({ message: SnackbarMessage.ANALYTIC_INSIGHT_SETUP });
          }
          if (
            syncType === ServiceBusEnum.CS_ISHEET_DEFAULT_DATA &&
            subType === "TRANSITION_PLAN"
          ) {
            showSnackbar({ message: SnackbarMessage.TRANSITION_PLAN });
          }
          if (
            syncType === ServiceBusEnum.CS_ISHEET_DEFAULT_DATA &&
            subType === "DELIVERY_PLAN"
          ) {
            showSnackbar({ message: SnackbarMessage.DELIVERY_PLAN });
          }
          if (
            syncType === ServiceBusEnum.CS_ISHEET_DEFAULT_DATA &&
            subType === "PROJECT_SETUP_TASK_TEMPLATES"
          ) {
            showSnackbar({
              message: SnackbarMessage.PROJECT_SETUP_TASK_TEMPLATES,
            });
          }
          if (
            syncType === ServiceBusEnum.CREATE_EXTERNAL_USER_CS &&
            subType === "IDM_AND_CEM"
          ) {
            showSnackbar({
              message: SnackbarMessage.IDM_AND_CEM,
            });
          }
          if (
            syncType === ServiceBusEnum.CREATE_EXTERNAL_USER_CS &&
            subType === "ASSIGN_USER_TO_SITE"
          ) {
            showSnackbar({
              message: SnackbarMessage.ASSIGN_USER_TO_SITE,
            });
          }
          if (syncType === ServiceBusEnum.CHECK_EXTERNAL_USER_STATUS) {
            showSnackbar({
              message: SnackbarMessage.CHECK_EXTERNAL_USER_STATUS,
            });
          }
          if (syncType === ServiceBusEnum.INTERNAL_USER_ASSIGNMENT_CS) {
            showSnackbar({ message: SnackbarMessage.PROVIDE_APP_ACCESS });
          }
          if (syncType === ServiceBusEnum.MEMBER_FIRM) {
            showSnackbar({ message: SnackbarMessage.MEMBER_FIRM });
          }
          if (syncType === ServiceBusEnum.RUN_SITES_API) {
            showSnackbar({ message: SnackbarMessage.CS_Run_Sites_API });
          }
          if (syncType === ServiceBusEnum.RUN_ISHEET_TRIGGER_API) {
            showSnackbar({ message: SnackbarMessage.RUN_ISHEET_TRIGGER_API });
          }
          if (type === "SYNC_MDM_PROJECT") {
            showSnackbar({
              message: SnackbarMessage.SYNC_MDM_PROJECTS,
            });
          }
          if (type === "SYNC_MDM_PHASE") {
            showSnackbar({
              message: SnackbarMessage.SYNC_MDM_PHASES,
            });
          }
          if (
            syncType === ServiceBusEnum.CREATE_EXTERNAL_USER_THIRD_PARTY &&
            subType === "IDM_AND_CEM"
          ) {
            showSnackbar({ message: SnackbarMessage.EX_IDM_AND_CEM });
          }
          if (
            syncType === ServiceBusEnum.CREATE_EXTERNAL_USER_THIRD_PARTY &&
            subType === "ASSIGN_USER_TO_SITE"
          ) {
            showSnackbar({ message: SnackbarMessage.ASSIGN_USER_TO_SITE });
          }
          if (syncType === ServiceBusEnum.CHECK_EXTERNAL_USER_STATUS) {
            showSnackbar({
              message: SnackbarMessage.CHECK_EXTERNAL_USER_STATUS,
            });
          }
          if (type === "SYNC_IMANAGE_WORKSPACE_PER_SITE") {
            showSnackbar({
              message: SnackbarMessage.SYNC_IMANAGE_WORKSPACE_PER_SITE,
            });
          }
        } catch (error) {
          showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        }
      }
      if (syncIsheets) {
        await taxPortalClientApi.taxPortal_UpdateIsheetDataTrigger(
          props?.data?.data?.hqSiteId,
          syncIsheets
        );
      }
    };

    const retryForSynMDM = async (celldata: any) => {
      const data: ServiceMessage = {
        siteId: props?.data?.data?.hqSiteId,
        userName: user?.profile.preferred_username,
        eventType: ServiceBusEnum.SYNC_MDM,
        mdmClientId: props?.data?.data?.mdmClientId,
        clientName: props?.data?.data?.name,
        appName: appName,
      };
      await taxPortalClientApi.taxPortal_InsertTopic([data]);
    };
    
    return (
      <div>
        {showRetry() ? (
          <Link component="button" variant="body2" onClick={retryProcess}>
            Run
          </Link>
        ) : showRun() ? (
          <Link
            component="button"
            variant="body2"
            style={{
              color:
                isSyncMdmInProgressOrError() &&
                !["SYNC_MDM", "SYNC_MDM_PROJECT", "SYNC_MDM_PHASE"].includes(
                  type
                )
                  ? "grey"
                  : "009cde",
              pointerEvents:
                isSyncMdmInProgressOrError() &&
                !["SYNC_MDM", "SYNC_MDM_PROJECT", "SYNC_MDM_PHASE"].includes(
                  type
                )
                  ? "none"
                  : "auto",
              opacity:
                isSyncMdmInProgressOrError() &&
                !["SYNC_MDM", "SYNC_MDM_PROJECT", "SYNC_MDM_PHASE"].includes(
                  type
                )
                  ? 0.5
                  : 1,
            }}
            onClick={showRun() ? retryProcess : retryForSynMDM}
          >
            Run
          </Link>
        ) : null}
      </div>
    );
  };

  const createdDateTime = (e: any) => {
    if (!isNull(e.data.status) && !isEmpty(e.data.status)) {
      if (e.data.created === null) {
        return null;
      } else {
        return (
          <div style={{ display: "flex" }}>
            <div>
              <div>{convertUTCTimeToLocal(e.data.created)}</div>
            </div>
          </div>
        );
      }
    } else return null;
  };
  const modifiedDateTime = (e: any) => {
    if (!isNull(e.data.status) && !isEmpty(e.data.status)) {
      if (e.data.created === null) {
        return null;
      } else {
        return (
          <div style={{ display: "flex" }}>
            <div>
              <div>{convertUTCTimeToLocal(e.data.lastModified)}</div>
            </div>
          </div>
        );
      }
    } else return null;
  };
  const buttonStyle: React.CSSProperties = {
    cursor: "pointer",
    padding: "5px 16px",
    transition: "color 0.3s, transform 0.3s, box-shadow 0.3s",
    margin: "10px 10px 10px 0px",
    color: "#333",
    fontFamily: "Arial, sans-serif",
    fontSize: "14px",
    border: "none",
    backgroundColor: "transparent",
    position: "relative",
    overflow: "hidden",
    zIndex: 1,
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
    borderRadius: "20px",
  };
  
  const activeButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: "#009cde",
    color: "#fff",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
  };
  const handleTabSelection = (tab: string) => setSelectedTab(tab);
  return (
    <div>
      <div>
        <button onClick={() => handleTabSelection("tab1")} style={selectedTab === "tab1" ? activeButtonStyle : buttonStyle}>
          Process Lists
        </button>
        <button onClick={() => handleTabSelection("tab2")} style={selectedTab === "tab2" ? activeButtonStyle : buttonStyle}>
        Assigned Processes
        </button>
      </div>
      {selectedTab === "tab1" ? (
        <ProcessListTab siteId={siteId} appName={appName} />
      ) : (
        <DataGrid
          ref={gridRef}
          {...dataGridConfig}
          dataSource={sortedDataSourceforCS}
          showBorders={true}
          allowColumnResizing={true}
          showRowLines={true}
          columnAutoWidth={true}
          onContentReady={onInitialized}
          onOptionChanged={(e) => {
            if (e.fullName?.includes('.width') && e.value !== e.previousValue) {
              onColumnResized(e);
            }
          }}
          noDataText={
            data.pending === true && data.fullfilled === false
              ? "Loading..."
              : "No data"
          }
        >
          <Scrolling
            mode="virtual"
            scrollByContent={true}
            rowRenderingMode="virtual"
            showScrollbar={true}
          />
          <Column
            dataField="type"
            width={400}
            caption="process"
            cellRender={cellFunction1}
            allowSorting={true}
          />
          <Column width={100} dataField="Run Process" cellRender={retryButton} />
          <Column width={80} dataField="status" caption="Status" />
          <Column width={400} dataField="message" caption="Message" />
          <Column
            width={200}
            cellRender={createdDateTime}
            dataField="created"
            caption="Initiated date and time"
          />
          <Column
            width={200}
            cellRender={modifiedDateTime}
            dataField="lastModified"
            caption="Completed date and time"
          />
         <Column dataField="createdBy" caption="Initiated by" 
          calculateCellValue={(rowData: any) => rowData?.lastModifiedBy || rowData?.createdBy || ""} />
        </DataGrid>
      )}
    </div>
  );
};
